import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, first, merge, race } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, RequirementViewModel, RequirementService, Int32StringTuple } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-requirement-details',
    templateUrl: './requirement-details.component.html',
    styleUrls: ['./requirement-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RequirementDetailsComponent implements OnInit, CanComponentDeactivate {

    requirement: RequirementViewModel;
    businessCategories$: Observable<Array<Int32StringTuple>>;
    users$: Observable<Array<StringStringTuple>>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;

    id: number;
    requirementForm: UntypedFormGroup;

    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private requirementService: RequirementService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('id'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();
        this.businessCategories$ = this.reportMetadataService.getBusinessCategoryMetadata();

        if (this.id) {
            this.requirementService.getRequirementById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {
            this.isNew = true;
            this.requirement = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    dataLoaded(requirement: RequirementViewModel) {
        this.requirement = requirement;

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.requirementForm.controls[name];
    }

    private buildForm() {
        this.requirementForm = this.formBuilder.group({
            requirementId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            description: ['', Validators.required],
            businessCategoryId: ['', Validators.required],
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.requirement) {
            this.isNew = true;
            this.requirement = this.initializeEmptyObject();
        }

        this.requirementForm.reset({
            requirementId: this.requirement.requirementId || '',
            name: this.requirement.name || '',
            businessCategoryId: this.requirement.businessCategoryId || '',
            description: this.requirement.description || '',
            createdBy: this.requirement.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.requirement.createdDate, true) || '',
            updatedBy: this.requirement.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.requirement.updatedDate, true) || '',
        });

        if (this.requirement.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.requirement.createdBy).pipe(map(t => t.item2));
        }
        if (this.requirement.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.requirement.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): RequirementViewModel {
        var result = <RequirementViewModel>{};
        return result;
    }

    private prepareSave(): RequirementViewModel {
        const formModel = this.requirementForm.value;

        return {
            requirementId: this.isNew ? null : formModel.requirementId,
            businessCategoryId: formModel.businessCategoryId,
            name: formModel.name,
            description: formModel.description
        };
    }

    public save() {
        if (!this.requirementForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const requirement = this.prepareSave();

        if (this.isNew) {
            this.requirementService.createRequirement(requirement).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.requirementService.updateRequirement(requirement.requirementId, requirement).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.requirementForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(merge(remain),
                first());

        } else {
            return of(true);
        }
    }


    private saveCompleted(requirement: RequirementViewModel) {
        this.requirement = requirement;
        this.id = requirement.requirementId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), requirement.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeReferenceData);
    }

}
